import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type SyntheticEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { sessionQueryKey } from '@/entities/session';
import { isUsernameShapeValid, UsernameField } from '@/features/auth';
import { changePassword, resendVerification, updateProfile } from '@/shared/api/auth';
import type { ProfileUpdate, User } from '@/shared/api/types';
import { i18n, languageStorageKey, supportedLanguages, type Language } from '@/shared/i18n';
import { avatarPresets, presetAvatar } from '@/shared/lib/avatar';
import {
  Avatar,
  Button,
  ErrorMessage,
  PasswordField,
  PasswordStrength,
  TextField,
} from '@/shared/ui';

import styles from './profile.module.css';

function useProfileUpdate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: ProfileUpdate) => updateProfile(body),
    onSuccess: (user) => {
      queryClient.setQueryData(sessionQueryKey, user);
    },
  });
}

/** Name and username. */
export function ProfileForm({ user }: { user: User }): React.JSX.Element {
  const { t } = useTranslation();
  const [displayName, setDisplayName] = useState(user.display_name);
  const [username, setUsername] = useState(user.username);
  const mutation = useProfileUpdate();

  const submit = (event: SyntheticEvent): void => {
    event.preventDefault();
    mutation.mutate({
      display_name: displayName,
      ...(username === user.username ? {} : { username }),
    });
  };

  return (
    <form className={styles.form} onSubmit={submit}>
      <TextField
        label={t('settings.displayName')}
        hint={t('settings.displayNameHint')}
        maxLength={100}
        value={displayName}
        onChange={(event) => {
          setDisplayName(event.target.value);
        }}
      />
      <UsernameField value={username} onChange={setUsername} current={user.username} />
      {mutation.isError && <ErrorMessage error={mutation.error} />}
      {mutation.isSuccess && (
        <p className={styles.ok} role="status">
          {t('common.saved')}
        </p>
      )}
      <Button
        type="submit"
        variant="primary"
        disabled={mutation.isPending || !isUsernameShapeValid(username)}
      >
        {t('common.save')}
      </Button>
    </form>
  );
}

/** One click on a preset changes the avatar. */
export function AvatarPicker({ user }: { user: User }): React.JSX.Element {
  const { t } = useTranslation();
  const mutation = useProfileUpdate();
  const selected =
    mutation.isPending && mutation.variables.avatar ? mutation.variables.avatar : user.avatar;

  return (
    <>
      <div className={styles.avatars} role="radiogroup" aria-label={t('settings.avatar')}>
        {Array.from({ length: avatarPresets }, (_, index) => {
          const avatar = presetAvatar(index + 1);

          return (
            <button
              key={avatar}
              type="button"
              role="radio"
              className={styles.avatarOption}
              aria-checked={avatar === selected}
              aria-label={t('settings.avatarOption', { n: index + 1 })}
              onClick={() => {
                mutation.mutate({ avatar });
              }}
            >
              <Avatar avatar={avatar} name={user.username} size={48} />
            </button>
          );
        })}
      </div>
      {mutation.isError && <ErrorMessage error={mutation.error} />}
    </>
  );
}

/**
 * Switches the interface language at once and remembers it in the profile.
 * Pass labelledBy when a heading already names the choice, so that the label
 * is not repeated on screen.
 */
export function LanguageSelect({
  user,
  labelledBy,
}: {
  user: User | null;
  labelledBy?: string;
}): React.JSX.Element {
  const { t } = useTranslation();
  const mutation = useProfileUpdate();
  const current = (i18n.resolvedLanguage ?? 'ru') as Language;

  return (
    <label className={styles.form}>
      {labelledBy === undefined && <span>{t('settings.language')}</span>}
      <select
        className={styles.select}
        {...(labelledBy === undefined ? {} : { 'aria-labelledby': labelledBy })}
        value={current}
        onChange={(event) => {
          const language = event.target.value as Language;
          void i18n.changeLanguage(language);

          try {
            localStorage.setItem(languageStorageKey, language);
          } catch {
            // Storage may be unavailable; the choice still applies to this visit.
          }

          if (user) {
            mutation.mutate({ locale: language });
          }
        }}
      >
        {supportedLanguages.map((language) => (
          <option key={language} value={language}>
            {t(`languages.${language}`)}
          </option>
        ))}
      </select>
    </label>
  );
}

/** Changes the password; other sessions end, this one stays. */
export function ChangePasswordForm({ user }: { user: User }): React.JSX.Element {
  const { t } = useTranslation();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const mutation = useMutation({
    mutationFn: () => changePassword({ current_password: current, new_password: next }),
    onSuccess: () => {
      setCurrent('');
      setNext('');
    },
  });

  const submit = (event: SyntheticEvent): void => {
    event.preventDefault();
    mutation.mutate();
  };

  return (
    <form className={styles.form} onSubmit={submit}>
      <input
        type="text"
        name="username"
        autoComplete="username"
        value={user.username}
        readOnly
        hidden
      />
      <PasswordField
        label={t('auth.currentPassword')}
        autoComplete="current-password"
        required
        value={current}
        onChange={(event) => {
          setCurrent(event.target.value);
        }}
      />
      <PasswordField
        label={t('auth.newPassword')}
        autoComplete="new-password"
        required
        minLength={8}
        hint={t('auth.passwordHint')}
        value={next}
        onChange={(event) => {
          setNext(event.target.value);
        }}
      />
      <PasswordStrength password={next} userInputs={[user.email, user.username]} />
      {mutation.isError && <ErrorMessage error={mutation.error} />}
      {mutation.isSuccess && (
        <p className={styles.ok} role="status">
          {t('settings.passwordChanged')}
        </p>
      )}
      <Button type="submit" variant="primary" disabled={mutation.isPending}>
        {t('settings.changePassword')}
      </Button>
    </form>
  );
}

/** Whether the address is confirmed, with a way to get a new link. */
export function EmailStatus({ user }: { user: User }): React.JSX.Element {
  const { t } = useTranslation();
  const mutation = useMutation({ mutationFn: resendVerification });

  return (
    <div className={styles.form}>
      <p className={styles.status}>
        {user.email} —{' '}
        <span className={user.email_verified ? styles.ok : undefined}>
          {user.email_verified ? t('settings.emailVerified') : t('settings.emailUnverified')}
        </span>
      </p>
      {!user.email_verified && (
        <ResendButton
          pending={mutation.isPending}
          sent={mutation.isSuccess}
          onClick={() => {
            mutation.mutate();
          }}
        />
      )}
      {mutation.isError && <ErrorMessage error={mutation.error} />}
    </div>
  );
}

/** The resend control shared by the settings page and the banner. */
export function ResendButton({
  pending,
  sent,
  onClick,
}: {
  pending: boolean;
  sent: boolean;
  onClick: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();

  if (sent) {
    return <span role="status">{t('auth.verifyResent')}</span>;
  }

  return (
    <Button disabled={pending} onClick={onClick}>
      {t('auth.verifyResend')}
    </Button>
  );
}
